import { PractitionerDirectory, Slot, Organization } from "../../shared/types";
import { UserIcon, ClockIcon, CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { apiPost } from "src/utils/api";
import { Card } from "../Card";

type SlotConfirmProps = {
  organization: Organization;
  selectedSlot: Slot;
  selectedPractitioner: PractitionerDirectory;
  patientId: string;
  onSchedule?: () => void;
};

const SlotConfirm = ({ organization, selectedSlot, selectedPractitioner, patientId, onSchedule = () => {} }: SlotConfirmProps) => {
  const [ reason, setReason ] = useState("");
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ error, setError ] = useState("");
  const [ success, setSuccess ] = useState(false);

  const handleBookAppointment = async () => {
    if (!reason.trim()) {
      setError("Please provide a reason for the appointment");
      return;
    }

    if (!patientId) {
      setError("Missing patient information");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      setSuccess(true);
      const response = await apiPost(`/api/${organization.id}/ehr/book-appointment`, {
        slot_id: selectedSlot.id,
        patient_id: patientId,
        reason: reason.trim(),
        service_type: selectedSlot.serviceType
      });

      const result = await response.json();
      if (result.valid) {
        setSuccess(true);
        setReason("");
        onSchedule();
      } else {
        setSuccess(false);
        setError("Failed to book appointment.");
      }
    } catch (_) {
      setError("Failed to book appointment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="p-12 shadow-xl rounded-2xl bg-white border-0">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircleIcon className="w-8 h-8 text-brand-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Appointment Booked Successfully!</h3>
            <p className="text-gray-600">Your appointment has been confirmed. You will receive a confirmation shortly.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 py-8">
      <Card className="p-8 shadow-xl rounded-2xl bg-white border-0">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircleIcon className="w-8 h-8 text-brand-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Confirm Your Appointment</h3>
          <p className="text-gray-600">Please review the details below before confirming</p>
        </div>

        <div className="space-y-6 mb-8">
          <h4 className="font-semibold text-gray-900 text-lg border-b border-gray-200 pb-3">
            Appointment Summary
          </h4>

          <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
            <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0">
              <UserIcon className="h-6 w-6 text-brand-600" />
            </div>
            <div>
              <p className="font-semibold text-lg text-gray-900">{selectedPractitioner.name}</p>
              <p className="text-sm text-gray-600">Healthcare Provider</p>
            </div>
          </div>

          <div className="flex items-start space-x-4 p-4 bg-brand-50 rounded-lg border border-brand-200">
            <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0">
              <ClockIcon className="h-6 w-6 text-brand-600" />
            </div>
            <div>
              <p className="font-semibold text-lg text-gray-900">
                {new Date(selectedSlot.start).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p className="text-sm text-gray-600">
                {new Date(selectedSlot.start).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })} - {new Date(selectedSlot.end).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </p>
              {selectedSlot.serviceType && (
                <p className="text-sm text-brand-700 font-medium mt-1">
                  Service: {selectedSlot.serviceType.text}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
              Reason for Appointment *
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please describe the reason for your appointment..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-brand-500 focus:border-brand-500 resize-none text-base"
              rows={4}
              required
            />
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <button
            className={`w-full py-3 px-6 rounded-lg text-base font-semibold transition-colors shadow-sm ${
              isSubmitting || !reason.trim()
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-gradient-to-r from-brand-500 to-brand-600 text-white hover:from-brand-600 hover:to-brand-700'
            }`}
            onClick={handleBookAppointment}
            disabled={isSubmitting || !reason.trim()}
          >
            {isSubmitting ? 'Booking Appointment...' : 'Confirm Appointment'}
          </button>
          <p className="text-xs text-gray-500 text-center mt-3">
            By confirming, you agree to our appointment policies and cancellation terms.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default SlotConfirm;
